import { Component, h, Prop, State } from "@stencil/core"
import { PaymentMethodEnum } from "../../enums/PaymentMethodEnum"
import { getLanguageHandler, getLocale, getSupportedLocaleData } from "../../languageHandler/LanguageHandler"
import { validatePaymentMethodEnum } from "../../helpers/Validators"
import { CardModel } from "../cardList/CardModel"
import { HorizontalMarkSizeEnum, HorizontalMarkSizeEnumsObj } from "../../enums/HorizontalMarkSizeEnum"
import { InfoMessageModel, MessageMaxLength } from "../infoMessage/InfoMessageModel"
import addCardIcon from "./addCardIcon.svg"
import { srcScreenError } from "../../helpers/CommonRenders"
import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"

/**
 * Entire screen with cards added to the Click to Pay System.
 */
@Component({
  tag: "card-list-screen",
  styleUrl: "CardListScreen.scss",
})
export class CardListScreen {
  /**
   * Merchant site preferred locale. Format based on ISO format for language (ISO 639-1) and country code
   * (ISO 3166-1 alpha-2). The language and country should be separated using an underscore ( _ ).
   * <br /><br />
   * **Supported locale**: en_GB, sv_SE, pl_PL
   * <br /><br />
   * @default en_GB
   */
  @Prop() locale?: string

  /**
   * Indicator which Click to Pay System is supported.
   */
  @Prop() paymentMethod: PaymentMethodEnum

  /**
   * Data source for alert which can be displayed on the AddCardScreen.
   */
  @Prop() infoMessage?: InfoMessageModel

  /**
   * The array of cards.
   */
  @Prop() cardList: CardModel[]

  /**
   * Indicator if the screen is loading.
   *
   * @default false
   */
  @Prop() isScreenLoading?: boolean

  /**
   * Indicator if the continue button is loading.
   *
   * @default false
   */
  @Prop() isContinueButtonLoading?: boolean

  /**
   * Value displayed near "not you?" clickable text. Should contain user's email address.
   */
  @Prop() userCredentials: string

  /**
   * Callback called after close button click.
   */
  @Prop() closeButtonOnClick?: () => void

  /**
   *  Callback called after add card button click.
   */
  @Prop() addCardButtonOnClick?: () => void

  /**
   * Callback called after continue button click.
   */
  @Prop() continueButtonOnClick?: (selectedCard: CardModel) => void

  /**
   * Callback called afer change user click.
   */
  @Prop() changeUserOnClick?: () => void

  @State() selectedCard: CardModel

  private horizontalMarkSize: HorizontalMarkSizeEnum
  private languageHandler: LanguageHandler
  private errorMessage: string[]
  private supportedLocale: string

  componentWillLoad() {
    this.errorMessage = []

    if (!this.paymentMethod) {
      this.errorMessage.push("'paymentMethod' prop is not defined")
    } else if (!validatePaymentMethodEnum(this.paymentMethod)) {
      this.errorMessage.push("'paymentMethod' prop is not valid")
    }

    if (this.errorMessage.length > 0) {
      return
    }

    this.horizontalMarkSize = HorizontalMarkSizeEnumsObj.S16

    const localeData = getSupportedLocaleData(this.locale)
    this.languageHandler = getLanguageHandler(localeData.lang)
    this.supportedLocale = getLocale(localeData)

    if (this.infoMessage && this.infoMessage.infoMessage.length > MessageMaxLength.CARD_LIST_SCREEN) {
      this.infoMessage.infoMessage = this.infoMessage.infoMessage.substring(0, MessageMaxLength.CARD_LIST_SCREEN)
    }
  }

  setSelectedCard = (cardModel: CardModel) => {
    this.selectedCard = cardModel
  }

  getSelectedCard = (cardModel: CardModel) => this.selectedCard.srcDigitalCardId === cardModel.srcDigitalCardId

  render() {
    if (this.errorMessage.length > 0) {
      return srcScreenError("Card List Screen", this.errorMessage)
    }

    const isContinueButtonDisabled = !this.cardList || this.cardList.length <= 0
    return (
      <div class="cardListScreen">
        <div class="closeButtonContainer">
          {typeof this.closeButtonOnClick === "function" && (
            <close-button closeButtonOnClick={this.closeButtonOnClick}></close-button>
          )}
        </div>
        <div class="topParent">
          <div class="topTitle">{this.languageHandler.getString("welcomeBackToClickToPay")}</div>
          <div class="topSubTitle">{this.languageHandler.getString("selectCard")}</div>
          <horizontal-mark
            class="srcMark"
            locale={this.supportedLocale}
            paymentMethod={this.paymentMethod}
            horizontalMarkSize={this.horizontalMarkSize}></horizontal-mark>
        </div>
        {this.infoMessage && Boolean(this.infoMessage.infoMessage) && (
          <div class={"infoText"}>
            <info-message infoMessage={this.infoMessage} />
          </div>
        )}
        <div class="cardList">
          <card-list
            expiredText={this.languageHandler.getString("expired")}
            isLoading={this.isScreenLoading}
            cardList={this.cardList}
            setSelectedCard={this.setSelectedCard}></card-list>
        </div>
        <div class={"listFadeDiv"}>
          <div class={"listFade"}></div>
        </div>
        <div class="buttonDiv">
          {typeof this.addCardButtonOnClick === "function" && (
            <button class="addNewCardButton" onClick={this.addCardButtonOnClick}>
              <img class={"addCardIcon"} src={addCardIcon}></img>
              {this.languageHandler.getString("addCard")}
            </button>
          )}

          {typeof this.continueButtonOnClick === "function" && (
            <button
              class="cardListContinueButton"
              disabled={this.isContinueButtonLoading || isContinueButtonDisabled}
              onClick={() => this.continueButtonOnClick && this.continueButtonOnClick(this.selectedCard)}>
              {this.isContinueButtonLoading === true ? (
                <loading-spinner loadingText={this.languageHandler.getString("processing")} />
              ) : (
                <div>{this.languageHandler.getString("continue")}</div>
              )}
            </button>
          )}
          {this.userCredentials && (
            <div class="bottomParent">
              <switch-user
                locale={this.supportedLocale}
                userCredentials={this.userCredentials}
                changeUserButtonOnClick={this.changeUserOnClick}
              />
            </div>
          )}
        </div>
      </div>
    )
  }
}
